/* ==========================================================================
   ps-consent.js — cookie / tracking consent for peak4beauty.com
   ---------------------------------------------------------------------------
   Plain English: the shop ships to the EU/EEA, so before we let anything
   non-essential track a visitor we have to ask. This shows a small banner at
   the bottom of the page, remembers the answer in localStorage, and — only if
   Shopify's own consent API happens to be present — passes the answer on to
   Shopify so its own pixels obey it.

   Rules this file keeps to:
     - no third-party code, no network requests of its own
     - never blocks the page: the banner is a strip, not an overlay
     - never shown again once a choice is stored
     - every Shopify API is feature-detected before it is called
     - keyboard reachable, announced through an aria-live region
     - the entrance animation lives in CSS behind prefers-reduced-motion
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'ps-consent-v1';
  var VALID = { accepted: true, rejected: true };

  /* ---------- storage (private mode can throw, so everything is guarded) --- */

  function readChoice() {
    var raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!parsed || !VALID[parsed.status]) return null;
    return parsed;
  }

  function writeChoice(status) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ status: status, ts: Date.now(), v: 1 })
      );
    } catch (e) {
      /* storage unavailable — the banner simply asks again next visit */
    }
  }

  /* ---------- hand the answer to Shopify, if and only if it is there ------- */

  function setShopifyConsent(granted) {
    var S = window.Shopify;
    if (!S) return;

    var payload = {
      analytics: granted,
      marketing: granted,
      preferences: granted,
      sale_of_data: granted
    };

    function send() {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.setTrackingConsent !== 'function') return;
      try {
        cp.setTrackingConsent(payload, function () {});
      } catch (e) {
        /* Shopify rejected it; the local choice still stands */
      }
    }

    if (S.customerPrivacy && typeof S.customerPrivacy.setTrackingConsent === 'function') {
      send();
      return;
    }

    if (typeof S.loadFeatures === 'function') {
      try {
        S.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (error) {
            if (!error) send();
          }
        );
      } catch (e) {
        /* feature loader unavailable — nothing else to do */
      }
    }
  }

  /* ---------- banner ------------------------------------------------------ */

  function init() {
    var root = document.querySelector('[data-ps-consent]');
    if (!root) return;

    var stored = readChoice();
    if (stored) {
      /* Already answered: keep the banner away and re-assert the answer so a
         late-loading Shopify pixel starts out on the right side of it. */
      setShopifyConsent(stored.status === 'accepted');
      return;
    }

    /* the live region deliberately sits OUTSIDE the banner: hidden elements are
       not announced, and we still want the confirmation read after the banner
       has gone away. */
    var status = document.querySelector('[data-ps-consent-status]');
    var acceptBtn = root.querySelector('[data-ps-consent-accept]');
    var rejectBtn = root.querySelector('[data-ps-consent-reject]');

    var openingAnnouncement = null;

    function announce(message) {
      if (status) status.textContent = message;
    }

    function choose(statusValue, message) {
      /* a very fast click must not be overwritten by the opening announcement */
      if (openingAnnouncement !== null) {
        window.clearTimeout(openingAnnouncement);
        openingAnnouncement = null;
      }
      writeChoice(statusValue);
      setShopifyConsent(statusValue === 'accepted');
      root.hidden = true;
      announce(message);
    }

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        choose('accepted', 'All cookies accepted. Thank you.');
      });
    }

    if (rejectBtn) {
      rejectBtn.addEventListener('click', function () {
        choose('rejected', 'Non-essential cookies rejected. Only essential cookies are used.');
      });
    }

    root.hidden = false;
    /* a live region only announces changes made after it is registered, so the
       opening message waits a beat rather than being written in the same tick */
    openingAnnouncement = window.setTimeout(function () {
      openingAnnouncement = null;
      announce('We would like your permission for non-essential cookies. Choose accept or reject at the bottom of the page.');
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
